/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path apihooks
 * @description Creates a MEA Query from database
 */
import axios, { AxiosResponse } from "axios";
import { uniqBy } from "lodash";
import { QueryKey, useQuery, UseQueryOptions } from "react-query";
import isDraftFilterAllowedAtLocation from "./../attributes/drafts/disabledPages";
import augmentQueryArguments from "./../attributes/parser/arguments";
import filterResultsetRow from "./../attributes/parser/resultset";
import transformJSONRows from "./transformJSONRows";
/**
 *
 * Example:
 ```typescript
 const {isLoading, data } = useMeaQuery("[SC.1234.5678]", "name, symbol");
 ```
 *
 * @param queryString
 * @param attributes
 * @param sortBy
 * @param options
 * @param jsonmode
 * @param queryKey
 * @param criteria
 * @returns
 */
const useMeaQuery = <T>(
  queryString: string,
  attributes: string,
  sortBy?: string,
  options?:
    | Omit<
        UseQueryOptions<
          {
            data: T;
          },
          unknown,
          {
            data: T;
          },
          QueryKey
        >,
        "queryKey" | "queryFn"
      >
    | undefined,
  jsonmode?: number | undefined,
  queryKey?: string,
  criteria?: string,
  queryOptions?: object
) => {
  const { status = "", ...restQueryOptions } = (queryOptions as any) || {};
  const key = `meaquery-${queryString}-${attributes}-${sortBy || ""}-${
    jsonmode || ""
  }-${queryKey || ""}`;
  const defaultValues = {
    keepPreviousData: true,
    staleTime: 60 * 1000,
    enabled:
      !queryString.includes("[undefined]") && !queryString.includes("[null]"),
  };
  return useQuery<{
    data: T;
  }>(
    `${key}-${isDraftFilterAllowedAtLocation()}`,
    async () => {
      /**
       * Add status flags to each period in the attribute set
       */
      const { newSet, disableFilter, newQueryAttrs } =
        augmentQueryArguments(attributes);
      const response: AxiosResponse<T> = await axios.post<T>("meaquery", {
        query: queryString,
        attributes: newQueryAttrs,
        sortBy,
        jsonmode,
        criteria,
        ...restQueryOptions,
      });
      const data = response.data as any;
      if (Array.isArray(data.data) && !disableFilter && status !== "1") {
        data.data = data.data.filter((row: any) => {
          filterResultsetRow(newSet, row);
          return true;
        });
      }
      transformJSONRows(response);
      if (Array.isArray(data.data)) {
        /**
         * If name is asked but no name is returned, filter away those items because they are items
         * where we do not have access rights
         */
        const attrList = attributes.split(",").map((v) => v.trim());
        if (attrList.includes("name")) {
          data.data = data.data.filter((elem: any) => elem.name);
        }
        return { data: uniqBy(data.data as any, "id") } as unknown as {
          data: T;
        };
      }
      return response.data as unknown as {
        data: T;
      };
    },
    options ? { ...defaultValues, ...options } : defaultValues
  );
};
export default useMeaQuery;
